import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

export type CancellationReason = 'wrong_adopter_info' | 'device_returned' | 'device_kept' | 'testing';

const CANCELLATION_REASONS: { value: CancellationReason; title: string; description: string }[] = [
  {
    value: 'wrong_adopter_info',
    title: 'I entered the wrong adopter info',
    description: 'Fix a typo in the email or phone — keeps the pet, reopens the adoption screen.',
  },
  {
    value: 'device_returned',
    title: 'Adopter kept the pet but returned the device',
    description: 'You have the device back; the adopter keeps the dog.',
  },
  {
    value: 'device_kept',
    title: "Adopter kept the device and I don't have it",
    description: "You don't have the device — track recovery from Stalled.",
  },
  {
    value: 'testing',
    title: 'I was just testing',
    description: 'No real adoption — return the device to stock.',
  },
];

interface CancelTransferModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  /**
   * Hands the chosen reason back to the page, which opens a per-reason
   * confirmation modal that performs the actual mutation. May be sync (just
   * routing) or async, so both return types are accepted.
   */
  onConfirm: (device: Device, reason: CancellationReason) => void | Promise<void>;
}

export default function CancelTransferModal({ open, onClose, device, onConfirm }: CancelTransferModalProps) {
  const [reason, setReason] = useState<CancellationReason | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!device) {
    return null;
  }

  const currentDevice = device;

  function handleClose() {
    if (submitting) {
      return;
    }
    setReason(null);
    onClose();
  }

  async function handleConfirm() {
    if (submitting || !reason) {
      return;
    }
    setSubmitting(true);
    try {
      // Hand off to the per-reason confirmation modal. If the handoff ever
      // throws we keep this modal open; the actual mutation (and its error
      // toast) lives in that follow-up modal.
      await onConfirm(currentDevice, reason);
      setReason(null);
      onClose();
    } catch {
      // Kept open on failure; toast already surfaced the error.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>Why are you canceling?</ModalTitle>
      <ModalSubtitle>
        Cancel the pending transfer for <b>{device.id}</b>
        {device.petName ? ` (${device.petName})` : ''}
        {device.adopterName ? ` to ${device.adopterName}` : ''}. Pick a reason — we&rsquo;ll handle the device the right
        way.
      </ModalSubtitle>

      <div className={styles.cancelReasonGroup} role="radiogroup" aria-label="Cancellation reason">
        {CANCELLATION_REASONS.map((option) => {
          const active = reason === option.value;
          return (
            <label
              key={option.value}
              className={`${styles.cancelReasonCard} ${active ? styles.cancelReasonCardActive : ''}`}
            >
              <input
                type="radio"
                name="cancellationReason"
                value={option.value}
                checked={active}
                onChange={() => setReason(option.value)}
                className={styles.cancelReasonInput}
                disabled={submitting}
              />
              <span className={styles.cancelReasonRadio} aria-hidden="true" />
              <span className={styles.cancelReasonContent}>
                <span className={styles.cancelReasonTitle}>{option.title}</span>
                <span className={styles.cancelReasonDescription}>{option.description}</span>
              </span>
            </label>
          );
        })}
      </div>

      <ModalActions>
        <Button variant="ghost" onClick={handleClose} disabled={submitting}>
          Keep transfer
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={submitting || !reason}>
          {submitting ? 'Working…' : 'Continue'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
